import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyledLayout, StyledTrialBannerWrapper } from './Layout.styles';
import SideBar from '../atoms/SideBar';
import Header from '../atoms/Header';
import TrialBanner from '../atoms/Header/TrialBanner';
import { useLocation } from 'react-router-dom';
import OnboardingTour from '../atoms/Storybook';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import CrowSetup from '../CrowSetup';

const TRIAL_BANNER_HIDE_COOKIE = 'houdiny_trial_banner_hidden_until';
const TRIAL_BANNER_HIDE_MS = 24 * 60 * 60 * 1000;
const TRIAL_BANNER_DURATION_DAYS = 7;

const getCookieValue = name => {
  const cookieEntry = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));
  return cookieEntry ? decodeURIComponent(cookieEntry.split('=')[1]) : null;
};

const setCookieValue = (name, value, maxAgeSeconds) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
};

const Layout = ({ children, showTemplate }) => {
  const location = useLocation();
  const noPaddingTop = location.pathname === '/campaign-manager' || location.pathname === '/unibox' || location.pathname.includes('/flow-builder') || location.pathname === '/dashboard';
  const crownEnabled = String(process.env.REACT_APP_CROW_ENABLED).toLowerCase() === 'true';
  const { user, loading_user, authReady } = useContextHook(AuthContext, v => ({
    user: v.user,
    loading_user: v.loading_user,
    authReady: v.authReady,
  }));
  const canRenderCrow = crownEnabled && user?.data?.featureAccess?.agentAccess;

  // ------- Trial banner (rendered globally so it shows on every page,
  // including ones where the Header is hidden: /unibox, /campaign-manager,
  // /automation/*). -------
  const [isTrialBannerVisible, setIsTrialBannerVisible] = useState(true);
  const trialBannerTimerRef = useRef(null);

  const syncTrialBannerVisibilityFromCookie = () => {
    const hiddenUntil = Number(getCookieValue(TRIAL_BANNER_HIDE_COOKIE));
    if (!hiddenUntil || Number.isNaN(hiddenUntil)) {
      setIsTrialBannerVisible(true);
      return;
    }

    const remainingMs = hiddenUntil - Date.now();
    if (remainingMs <= 0) {
      setIsTrialBannerVisible(true);
      return;
    }

    setIsTrialBannerVisible(false);
    if (trialBannerTimerRef.current) {
      clearTimeout(trialBannerTimerRef.current);
    }
    trialBannerTimerRef.current = setTimeout(() => {
      setIsTrialBannerVisible(true);
    }, remainingMs);
  };

  const isSubscriptionActive = user?.data?.status === 'subscription_active';
  const isTrialBannerExpired = useMemo(() => {
    const createdAt = user?.data?.created_at;
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return false;
    const elapsedMs = Date.now() - createdDate.getTime();
    const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
    return elapsedDays >= TRIAL_BANNER_DURATION_DAYS;
  }, [user?.data?.created_at]);

  const handleCloseTrialBanner = () => {
    const hiddenUntil = Date.now() + TRIAL_BANNER_HIDE_MS;
    setCookieValue(TRIAL_BANNER_HIDE_COOKIE, String(hiddenUntil), Math.ceil(TRIAL_BANNER_HIDE_MS / 1000));
    setIsTrialBannerVisible(false);
    if (trialBannerTimerRef.current) {
      clearTimeout(trialBannerTimerRef.current);
    }
    trialBannerTimerRef.current = setTimeout(() => {
      setIsTrialBannerVisible(true);
    }, TRIAL_BANNER_HIDE_MS);
  };

  const handleTrialBannerCloseStart = () => {
    document.body.classList.remove('trial-banner-visible');
  };

  useEffect(() => {
    syncTrialBannerVisibilityFromCookie();
    return () => {
      if (trialBannerTimerRef.current) {
        clearTimeout(trialBannerTimerRef.current);
      }
    };
  }, []);

  const isUserReadyForTrialBanner = authReady && !loading_user && Boolean(user?.data?.created_at);
  const shouldShowTrialBanner = Boolean(
    showTemplate
    && isUserReadyForTrialBanner
    && isTrialBannerVisible
    && !isSubscriptionActive
    && !isTrialBannerExpired
    && location.pathname !== '/create-campaign',
  );

  useEffect(() => {
    const bodyClassName = 'trial-banner-visible';
    if (shouldShowTrialBanner) {
      document.body.classList.add(bodyClassName);
    } else {
      document.body.classList.remove(bodyClassName);
    }
    return () => {
      document.body.classList.remove(bodyClassName);
    };
  }, [shouldShowTrialBanner]);

  return (
    <>
      {shouldShowTrialBanner && (
        <StyledTrialBannerWrapper>
          <TrialBanner
            onClose={handleCloseTrialBanner}
            onCloseStart={handleTrialBannerCloseStart}
            user={user}
          />
        </StyledTrialBannerWrapper>
      )}
      <StyledLayout $showlayout={showTemplate} $nopaddingTop={noPaddingTop}>
        <span className="overlay" />
        {showTemplate ? (
          <>
            <OnboardingTour />
            <SideBar />
            <div className="children">
              {/* Hide Header on /unibox and /automation/* pages */}
              {location.pathname !== '/unibox' && !location.pathname.startsWith('/automation/') && location.pathname !== '/campaign-manager' && location.pathname !== '/dashboard' && <Header />}
              {children}
              {canRenderCrow && (
                <CrowSetup />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="children">{children}
            </div>
          </>
        )}
      </StyledLayout>
    </>
  );
};

export default Layout;
